/* import { isDefined } from "../infra3d"; */
function isDefined(object) {
    return !(typeof object === "undefined");
}
/**
 * An easier way to refer to the priority of a cache item
 * @enum {number}
 */
const CachePriority = {
    LOW: 1,
    NORMAL: 2,
    HIGH: 4,
};
/**
 * @typedef {{
 *     expirationAbsolute: Date,
 *     expirationSliding: number,
 *     priority: CachePriority,
 *     callback: function(string, Object)
 * }}
 */
let CacheSetItemOptions;
/**
 * Cache Object class
 * This class is based on jscache, available at https://github.com/monsur/jscache/
 * and is also released under MIT license
 * (see LICENSE file and CREDITS file for more information)
 * Copyright (c) 2007 Monsur Hossain (http://monsur.hossai.in)
 * This version is slightly modified for the OpenWebGlobe cache
 * Modifications from original code:
 * If the item has a function called "Destroy()" it is called when purged
 * from cache. This way you don't have to specify a callback function for
 * every item. (i.e. using prototype function to save memory).
 * @class Cache
 * @constructor
 * @param {number} maxSize The maximum size of the cache (or -1 for no max).
 * @param {boolean} debug Whether to log events
 */
export class Cache {
    /**
     * @method constructor
     * @param {number} maxSize The maximum size of the cache (or -1 for no max).
     * @param {boolean} debug Whether to log events
     */
    constructor(maxSize, debug) {
        this._items = {};
        this._count = 0;
        this.fillFactor = 0.75;
        this._maxSize = maxSize || -1;
        this._debug = debug || false;
        this._count = 0;
        const fillFactor = 0.75;
        this._purgeSize = Math.round(this._maxSize * fillFactor);
        this._stats = {};
        this._stats["hits"] = 0;
        this._stats["misses"] = 0;
        this._log("Initialized cache with size " + maxSize);
    }
    /**
     * Returns the number of cached Items.
     * @method getSize
     * @return {number} The number of cached Items.
     */
    getSize() {
        return this._count;
    }
    /**
     * Retrieves an item from the cache.
     * @method getItem
     * @param {string} key The key to retrieve.
     * @return {Object|null} The item, or null if it doesn't exist.
     */
    getItem(key) {
        // retrieve the item from the cache
        let item = this._items[key];
        if (isDefined(item) && item !== null) {
            if (!this._isExpired(item)) {
                // if the item is not expired
                // update its last accessed date
                item.lastAccessed = new Date().getTime();
                if (this._lastAccess == null || this._lastAccess < item.lastAccessed) {
                    this._lastAccess = JSON.parse(JSON.stringify(item.lastAccessed));
                }
            }
            else {
                // if the item is expired, remove it from the cache
                this._removeItem(key);
                item = null;
            }
        }
        // return the item value (if it exists), or null
        const returnVal = item ? item.value : null;
        if (returnVal) {
            this._stats["hits"]++;
            this._log("Cache HIT for key " + key);
        }
        else {
            this._stats["misses"]++;
            this._log("Cache MISS for key " + key);
        }
        return returnVal;
    }
    /**
     * Sets an item in the cache.
     * @method setItem
     * @param {string} key The key to refer to the item.
     * @param {Object} value The item to cache.
     * @param {Object} opt_options an optional object which controls various caching
     *    options:
     *      expirationAbsolute: the datetime when the item should expire
     *      expirationSliding: an integer representing the seconds since
     *                         the last cache access after which the item
     *                         should expire
     *      priority: How important it is to leave this item in the cache.
     *                You can use the values CachePriority.Low, .Normal, or
     *                .High, or you can just use an integer.  Note that
     *                placing a priority on an item does not guarantee
     *                it will remain in cache.  It can still be purged if
     *                an expiration is hit, or if the cache is full.
     *      callback: A function that gets called when the item is purged
     *                from cache.  The key and value of the removed item
     *                are passed as parameters to the callback function.
     */
    setItem(key, value, opt_options) {
        // add a new cache item to the cache
        if (isDefined(this._items[key]) && this._items[key] !== null) {
            this._removeItem(key);
        }
        this._addItem(new CacheItem(key, value, opt_options));
        this._log("Setting key " + key);
        // if the cache is full, purge it
        if (this._maxSize > 0 && this._count > this._maxSize) {
            setTimeout(() => {
                this._purge.call(this);
            }, 10);
        }
    }
    /**
     * Removes all items from the cache.
     * @method clear
     */
    clear() {
        // loop through each item in the cache and remove it
        for (const key in this._items) {
            this._removeItem(key);
        }
        this._log("Cache cleared");
    }
    /**
     * Returns hits and misses on the cache.
     * @method getStats
     * @return {Object}
     */
    getStats() {
        return this._stats;
    }
    /**
     * Returns an HTML string representation of the cache.
     * @method toHtmlString
     * @return {string}
     */
    toHtmlString() {
        let returnStr = this._count + " item(s) in cache<br /><ul>";
        for (const key in this._items) {
            const item = this._items[key];
            returnStr = returnStr + "<li>" + item.key.toString() + " = " + item.value.toString() + "</li>";
        }
        returnStr = returnStr + "</ul>";
        return returnStr;
    }
    /**
     * Removes expired items from the cache.
     * @method _purge
     * @private
     */
    _purge() {
        let tmparray = [];
        // loop through the cache, expire items that should be expired
        // otherwise, add the item to an array
        for (const key in this._items) {
            const item = this._items[key];
            if (this._isExpired(item)) {
                this._removeItem(key);
            }
            else {
                tmparray.push(item);
            }
        }
        if (tmparray.length > this._purgeSize) {
            // sort this array based on cache priority and the last accessed date
            tmparray = tmparray.sort(function (a, b) {
                if (a.options.priority != b.options.priority) {
                    return b.options.priority - a.options.priority;
                }
                else {
                    return b.lastAccessed - a.lastAccessed;
                }
            });
            // remove items from the end of the array
            while (tmparray.length > this._purgeSize) {
                const ritem = tmparray.pop();
                this._removeItem(ritem.key);
            }
        }
        this._log("Purged cached");
    }
    /**
     * Add an item to the cache.
     * @method addItem_
     * @param {CacheItem} item The cache item to add.
     * @private
     */
    _addItem(item) {
        this._items[item["key"]] = item;
        this._count++;
    }
    /**
     * Remove an item from the cache, call the callback function (if it exists).
     * @method _removeItem
     * @param {string} key The key of the item to remove
     * @private
     */
    _removeItem(key) {
        const item = this._items[key];
        delete this._items[key];
        this._count--;
        this._log("removed key " + key);
        // if there is a callback function, call it at the end of execution
        if (isDefined(item.options) && isDefined(item.options.callback) && item.options.callback !== null) {
            setTimeout(function () {
                item.options.callback.call(null, item.key, item.value);
            }, 0);
        }
        if (isDefined(item.value) && item.value !== null) {
            if (isDefined(item.value.destroy) && item.value.destroy !== null) {
                setTimeout(function () {
                    item.value.destroy();
                }, 0);
            }
        }
    }
    /**
     * @method isExpired_
     * @param {Object} item A cache item.
     * @return {boolean} True if the item is expired
     * @private
     */
    _isExpired(item) {
        const now = new Date().getTime();
        let expired = false;
        if (item["options"].expirationAbsolute && item["options"].expirationAbsolute < now) {
            // if the absolute expiration has passed, expire the item
            expired = true;
        }
        if (!expired && item["options"].expirationSliding) {
            // if the sliding expiration has passed, expire the item
            const lastAccess = item["lastAccessed"] + item["options"].expirationSliding * 1000;
            if (lastAccess < now)
                expired = true;
        }
        return expired;
    }
    /**
     * Logs a message if debug is set to true.
     * @method log_
     * @param {string} msg The message to log.
     * @private
     */
    _log(msg) {
        if (this._debug)
            console.log(msg);
    }
}
class CacheItem {
    constructor(k, v, o) {
        if (k === null || k === "")
            throw new Error("key cannot be null or empty");
        this.key = k;
        this.value = v;
        if (o === null)
            o = {};
        if (isDefined(o.expirationAbsolute) && o.expirationAbsolute !== null) {
            o.expirationAbsolute = o.expirationAbsolute.getTime();
        }
        if (isDefined(o.priority) && o.priority === null) {
            o.priority = CachePriority.NORMAL;
        }
        this.options = o;
        this.lastAccessed = new Date().getTime();
    }
}
//# sourceMappingURL=cache.js.map